import { i18n } from "../translate/i18n";

const MEDIA_EXT_RE = /\.(ogg|mp3|mp4|webm|jpe?g|png|webp|gif|pdf|doc|docx|xls|xlsx|ppt|pptx|zip|txt|csv|md)$/i;

export const isMediaFilename = (str) => {
  if (!str || typeof str !== "string") return false;
  if (str.includes(" ")) return false;
  return MEDIA_EXT_RE.test(str);
};

export const inferMediaPlaceholder = (str) => {
  if (!isMediaFilename(str)) return null;
  const lower = str.toLowerCase();

  if (lower.startsWith("sticker-") || (lower.endsWith(".webp") && !lower.startsWith("image-"))) {
    return { icon: "🎴", text: i18n.t("mediaPlaceholder.sticker") };
  }
  if (lower.startsWith("audio-") || /\.(ogg|mp3)$/i.test(lower)) {
    return { icon: "🎤", text: i18n.t("mediaPlaceholder.audio") };
  }
  if (lower.startsWith("image-") || /\.(jpe?g|png|gif)$/i.test(lower)) {
    return { icon: "📷", text: i18n.t("mediaPlaceholder.image") };
  }
  if (lower.startsWith("video-") || /\.(mp4|webm)$/i.test(lower)) {
    return { icon: "🎬", text: i18n.t("mediaPlaceholder.video") };
  }
  if (lower.startsWith("document-") || /\.(pdf|docx?|xlsx?|pptx?|zip|txt|csv|md)$/i.test(lower)) {
    return { icon: "📄", text: i18n.t("mediaPlaceholder.document") };
  }
  return { icon: "📎", text: i18n.t("mediaPlaceholder.attachment") };
};

export const formatLastMessage = (body) => {
  if (!body) return body;
  const placeholder = inferMediaPlaceholder(body);
  return placeholder ? `${placeholder.icon} ${placeholder.text}` : body;
};

// Regex del patrón: <baseName>.<5charsRandom>.<ext>
// randomId = 5 caracteres alfanuméricos (a-zA-Z0-9) según saveMediaFile:106
const SUFFIXED_FILENAME_RE = /^(.+)\.([a-zA-Z0-9]{5})\.([a-zA-Z0-9]{2,5})$/;

// Filename autogenerado por WAIA cuando no vino con nombre real
const AUTO_GENERATED_RE = /^(document|audio|image|video|sticker)-\d+/;

/**
 * Extrae el nombre real del archivo desde el body del mensaje.
 * - "Contrato.abc12.docx" → "Contrato.docx"
 * - "DNI 22123229.pIb8j.pdf" → "DNI 22123229.pdf"
 * - "document-1784212440356.lPpxh.pdf" → null (autogenerado, sin nombre útil)
 * - "" o null → null
 * Devuelve null cuando NO hay nombre real que mostrar.
 */
export const parseDocumentName = (body) => {
  if (!body || typeof body !== "string") return null;
  if (AUTO_GENERATED_RE.test(body)) return null;

  const match = body.match(SUFFIXED_FILENAME_RE);
  if (match) {
    const [, base, , ext] = match;
    return `${base}.${ext}`;
  }
  // Body sin sufijo — quizás ya viene limpio (algún caller no aplicó saveMediaFile)
  // Solo devolver si parece un filename razonable (no un caption largo)
  if (body.length < 200 && !body.includes("\n")) return body;
  return null;
};

/**
 * Extrae la extensión (minúscula) del mediaUrl.
 * - "document.abc12.pdf" → "pdf"
 * - "" o null → null
 */
export const getDocumentExtension = (mediaUrl) => {
  if (!mediaUrl || typeof mediaUrl !== "string") return null;
  const m = mediaUrl.match(/\.([a-zA-Z0-9]{2,5})$/);
  return m ? m[1].toLowerCase() : null;
};

/**
 * Devuelve el ícono según extensión.
 */
export const getDocumentIcon = (ext) => {
  if (!ext) return "📄";
  if (ext === "pdf") return "📕";
  if (["doc", "docx"].includes(ext)) return "📝";
  if (["xls", "xlsx", "csv"].includes(ext)) return "📊";
  if (["ppt", "pptx"].includes(ext)) return "📽️";
  if (["zip", "rar", "7z"].includes(ext)) return "🗜️";
  return "📄";
};

/**
 * True si la extensión soporta preview inline en browser (solo PDF por ahora).
 */
export const canPreviewDocument = (ext) => ext === "pdf";
